import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface LocationMapPreviewProps {
  locationName: string;
  latitude: number;
  longitude: number;
  className?: string;
}

const MAPBOX_TOKEN = "pk.eyJ1IjoiZGlzY28tZ2xvYmFsIiwiYSI6ImNtY2wzNnBkczA2ZGEyaXEwOGZ4eHIwZGYifQ.sg2Gt1rGvYqF46r84brYPw";

export const LocationMapPreview = ({ 
  locationName, 
  latitude, 
  longitude, 
  className = "" 
}: LocationMapPreviewProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);
  
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
  
  const handleMapClick = () => {
    window.open(googleMapsUrl, '_blank');
  };

  const initializeMap = () => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [longitude, latitude],
      zoom: 15,
      interactive: false // Disable map interaction since clicking opens Google Maps
    });

    // Add marker
    new mapboxgl.Marker({ color: '#ef4444' })
      .setLngLat([longitude, latitude])
      .addTo(map.current);

    setIsMapInitialized(true);
  };

  useEffect(() => {
    initializeMap();

    return () => {
      map.current?.remove();
    };
  }, [latitude, longitude]);

  // Update map center when coordinates change
  useEffect(() => {
    if (map.current && isMapInitialized) {
      map.current.setCenter([longitude, latitude]);
      
      // Remove existing markers and add new one
      const markers = document.querySelectorAll('.mapboxgl-marker');
      markers.forEach(marker => marker.remove());
      
      new mapboxgl.Marker({ color: '#ef4444' })
        .setLngLat([longitude, latitude])
        .addTo(map.current!);
    }
  }, [latitude, longitude, isMapInitialized]);

  return (
    <div className={`bg-white/10 backdrop-blur-md rounded-xl border border-white/30 overflow-hidden cursor-pointer hover:bg-white/15 transition-all duration-200 ${className}`}>
      <div 
        onClick={handleMapClick}
        className="relative h-32 overflow-hidden"
      >
        {/* Mapbox container */}
        <div ref={mapContainer} className="absolute inset-0 w-full h-full" />
        
        {/* Overlay for location name and click indicator */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        
        {/* Click to open indicator */}
        <div className="absolute top-2 right-2 bg-black/20 rounded-full p-1">
          <ExternalLink className="w-3 h-3 text-white" />
        </div>
        
        {/* Location name overlay */}
        <div className="absolute bottom-2 left-2">
          <div className="text-white text-sm font-medium drop-shadow-lg">
            {locationName}
          </div>
        </div>
      </div>
      
      <div className="p-3 text-center md:bg-transparent bg-background/90">
        <p className="text-white md:text-white text-foreground text-xs opacity-80">
          Click to open in Google Maps
        </p>
      </div>
    </div>
  );
};